import { css } from "@emotion/css";
import { ActionButtonType } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, ProcessTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { Repository } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import type { ProcessTemplateProducerActionDispatcher } from "~/analytics/Analytics";
import { Action } from "~/analytics/Analytics";
import { AddTemplateParameterButton, OpenReorderParametersButton, TemplateParametersOnboarding } from "~/areas/platformHub/TemplateParameters";
import { defaultScopeValues } from "~/areas/projects/components/Process/ProcessTemplates/TemplateParameterHelpers";
import { TemplateParametersTable } from "./TemplateParametersTable";
interface ProducerProcessTemplateParametersTabProps {
    parameters: ProcessTemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
    dispatchProcessTemplateAction: ProcessTemplateProducerActionDispatcher | undefined;
    processTemplateLink: string | undefined;
}
export function ProducerProcessTemplateParametersTab({ parameters, onChange, dispatchProcessTemplateAction, processTemplateLink }: ProducerProcessTemplateParametersTabProps) {
    const { result: environments } = useQuery((repository) => repository.PlatformHub.getEnvironmentSummaries(), [], "Get environment summaries");
    const { result: platformHubAccounts } = useQuery((repository) => repository.PlatformHubAccounts.get({ Take: Repository.takeAll }), [], "Get all platform hub accounts");
    const availableScopeValues = useMemo(() => ({ ...defaultScopeValues, Environments: environments?.map((e) => ({ Id: `${e.SpaceId}/${e.Id}`, Name: `${e.Name} (${e.SpaceName})` })) ?? [] }), [environments]);
    const onAdd = (parameter: ProcessTemplateParameterResource) => {
        onChange([...parameters, parameter]);
        if (dispatchProcessTemplateAction) {
            dispatchProcessTemplateAction("Process Template Parameter Created", {
                type: "Process Template Parameter Created",
                action: Action.Add,
                controlType: parameter.DisplaySettings["Octopus.ControlType"],
                hasDefaultValue: parameter.Values.length > 0,
            });
        }
    };
    if (parameters.length === 0) {
        return (<div className={styles.emptyStateContainer}>
                <TemplateParametersOnboarding onAdd={onAdd} parameters={parameters}/>
            </div>);
    }
    if (!platformHubAccounts || !environments) {
        return null;
    }
    return (<>
            <div className={styles.headerContainer}>
                <OpenReorderParametersButton parameters={parameters} onChange={onChange}/>
                <AddTemplateParameterButton parameters={parameters} onAdd={onAdd} type={ActionButtonType.Secondary}/>
            </div>
            <TemplateParametersTable parameters={parameters} onChange={onChange} availableScopeValues={availableScopeValues} platformHubAccounts={platformHubAccounts.Items} templateLink={processTemplateLink}/>
        </>);
}
export const styles = {
    emptyStateContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
    }),
    headerContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "end",
        gap: space[12],
        paddingRight: space[16],
        paddingTop: space[24],
        paddingBottom: space[24],
    }),
};
